import { IGameLauncherCommand } from './gamelaunchercommand.types';
import { IPlugin as IUAPlugin } from '~/plugins/userAttribution/types';

export interface IConstructorPayload {
  getUa: () => IUAPlugin;
}

export class GameLauncherService {
  url = 'ws://localhost:' + (process.env.galaLauncherPort || '32123');
  currentVersion: string | null = null;
  lastConnected: number | null = null;
  autoReconnect = false;
  isAwaitingLauncher = false;
  isInstallingGame = false;
  isDownloadingGame = false;
  downloadPercent: number | null = null;
  gameNameInProgress: string | undefined | null = null; // name of game in progress of downloading or installing
  socket: any;
  listeners: any = {};
  timeoutHandler: any;
  getUa!: () => IUAPlugin;

  constructor(payload: IConstructorPayload) {
    this.getUa = payload.getUa;
    this.connect();
  }

  get $ua() {
    return this.getUa();
  }

  setVersion(version: string) {
    this.currentVersion = version;
  }

  getDisplayErrorMessage(eventMessage: string) {
    let errMessage!: string;

    if (eventMessage === 'Not connected') {
      errMessage =
        'The Gala Launcher is no longer connected, please re-establish internet connection.';
    } else if (
      eventMessage === 'jwt malformed' ||
      eventMessage === 'jwt expired' ||
      eventMessage === 'Missing jwt'
    ) {
      errMessage = 'Authorization failed. Log in and try again.';
    } else if (eventMessage === 'Platform not supported') {
      errMessage = eventMessage;
    } else if (eventMessage === 'Launch failed') {
      errMessage =
        'The game has failed to launch. If issue persists, please contact Customer Support.';
    } else if (eventMessage === 'Install timed out') {
      errMessage =
        'The application has encountered an unknown error, please contact Customer Support for assistance.';
    } else {
      // console.log(eventMessage)
    }

    return errMessage;
  }

  handleError(message: any) {
    const errMessage = message.error;
    this.isDownloadingGame = false;
    this.isInstallingGame = false;
    this.downloadPercent = 0;

    // UA tracking
    this.$ua.trackLauncherErrorEvent(message);

    if (errMessage === 'Not connected') {
      if (this.lastConnected) {
        this.autoReconnect = true;

        // UA tracking
        this.$ua.trackLauncherAttemptingToAutoReconnectEvent({
          lastConnected: this.lastConnected,
          error: message,
        });

        this.connect();
      }
    }
  }

  handleDownloadProgress(message: any) {
    this.downloadPercent = message.value;

    this.isDownloadingGame = true;

    if (!this.gameNameInProgress) {
      this.gameNameInProgress = message.gameName;
    }

    if (message.value >= 100) {
      this.isDownloadingGame = false;
      this.isInstallingGame = true;

      // UA tracking
      this.$ua.trackGameDownloadCompleteEvent({
        game: this.gameNameInProgress,
      });
    } else {
      // UA tracking
      this.$ua.trackGameDownloadProgressEvent({
        game: this.gameNameInProgress,
        progress: message.value,
      });
    }
  }

  handleAwaitingInstall(message: any) {
    this.isInstallingGame = true;

    if (!this.gameNameInProgress) {
      this.gameNameInProgress = message.gameName;
    }

    // UA tracking
    this.$ua.trackGameAwaitingInstallEvent({
      userAgent: navigator.userAgent,
      game: this.gameNameInProgress,
    });
  }

  handleInstallComplete(message: any) {
    // UA tracking
    this.$ua.trackGameInstallCompleteEvent({
      userAgent: navigator.userAgent,
      game: this.gameNameInProgress,
    });

    this.isDownloadingGame = false;
    this.isInstallingGame = false;
    this.downloadPercent = 0;
    this.gameNameInProgress = null;
  }

  handleUpdateComplete(message: any) {
    // UA tracking
    this.$ua.trackGameUpdateCompleteEvent({
      userAgent: navigator.userAgent,
      game: this.gameNameInProgress,
    });

    this.isDownloadingGame = false;
    this.isInstallingGame = false;
    this.downloadPercent = 0;
    this.gameNameInProgress = null;
  }

  handleVersion(message: any) {
    this.currentVersion = message.version;

    // UA tracking
    this.$ua.trackLauncherSetVersionCompleteEvent({
      userAgent: navigator.userAgent,
      currentVersion: this.currentVersion,
    });
  }

  connect() {
    try {
      // UA tracking
      this.$ua.trackLauncherAttemptingToConnectEvent({
        lastConnected: this.lastConnected,
      });

      this.socket = new WebSocket(this.url);
    } catch (err) {}

    this.socket.onerror = (err: any) => {};

    this.socket.addEventListener('error', (event: any) => {});
    this.socket.addEventListener('open', async (event: any) => {
      this.lastConnected = Date.now();
      this.isAwaitingLauncher = false;

      // UA tracking
      this.$ua.trackLauncherConnectionEstablishedEvent({
        lastConnected: this.lastConnected,
      });

      await this.send({ command: 'version' });

      this.dispatchEvent({
        type: 'open',
      });

      if (this.timeoutHandler) {
        clearTimeout(this.timeoutHandler);
      }
    });
    this.socket.addEventListener('close', (event: any) => {
      this.dispatchEvent({
        type: 'close',
      });

      if (this.lastConnected) {
        this.autoReconnect = true;
        this.isAwaitingLauncher = true;
      }

      // UA tracking
      const uaPayload = { lastConnected: this.lastConnected };
      this.$ua.trackLauncherConnectionClosedEvent(uaPayload);

      if (event.target.readyState !== WebSocket.OPEN && this.autoReconnect) {
        // UA tracking
        this.$ua.trackLauncherAttemptingToAutoReconnectEvent(uaPayload);
        this.timeoutHandler = setTimeout(() => {
          this.connect();
        }, 3000);
      }
    });

    this.socket.onmessage = (evt: any) => {
      const message = JSON.parse(evt.data);
      this.dispatchEvent({
        type: 'message',
        message,
      });

      if (message.error) {
        this.handleError(message);
      } else if (message.command === 'version') {
        this.handleVersion(message);
      } else if (message.type === 'downloadProgress') {
        this.handleDownloadProgress(message);
      } else if (message.type === 'awaitingInstall') {
        this.handleAwaitingInstall(message);
      } else if (message.type === 'installComplete') {
        this.handleInstallComplete(message);
      } else if (message.type === 'updateComplete') {
        this.handleUpdateComplete(message);
      } else if (message.type === 'areGamesInstalled') {
        // UA tracking
        this.$ua.trackLauncherInstalledGamesCheckCompleteEvent(message);
      } else if (
        message.command === 'launchGame' &&
        !message.launchSuccessful
      ) {
        this.handleError({
          error: 'Launch failed',
        });
      }
    };
  }

  async send(command: IGameLauncherCommand): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.socket.readyState !== WebSocket.OPEN) {
        return reject('Not connected');
      }

      this.socket.addEventListener('error', (event: any) => {
        reject(event.message);
      });

      let listenerHandle: any;
      listenerHandle = (evt: any) => {
        if (
          command &&
          command.command &&
          evt &&
          evt.message &&
          evt.message.command &&
          command.command !== evt.message.command
        ) {
          // ignore message not for this command
          return;
        }

        try {
          resolve(evt.message);
          this.removeEventListener('message', listenerHandle);
        } catch (err) {
          reject('Invalid response');
          this.removeEventListener('message', listenerHandle);
        }
      };

      try {
        const message = JSON.stringify(command);
        this.socket.send(message);
        resolve(true);
      } catch (err) {
        let errMsg = 'Failed to send command.';
        if (err instanceof Error) {
          errMsg = err.message;
        }
        return reject(errMsg);
      }
    });
  }

  async installGame(
    command: IGameLauncherCommand,
    onDownloadProgress: (percent: number) => void,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.socket.readyState !== WebSocket.OPEN) {
        const errMsg = 'Not connected';

        // UA tracking
        this.$ua.trackGameInstallErrorEvent({
          userAgent: navigator.userAgent,
          game: command.params?.gameName,
          message: errMsg,
          launcherCurrentVersion: this.currentVersion,
        });

        return reject(errMsg);
      }

      let listenerHandle: any;
      listenerHandle = (evt: any) => {
        if (
          command &&
          command.command &&
          evt &&
          evt.message &&
          evt.message.command &&
          command.command !== evt.message.command
        ) {
          // ignore message not for this command
          return;
        }

        try {
          const message = evt.message;

          if (message.type === 'complete') {
            resolve(message);
            this.removeEventListener('message', listenerHandle);
            this.isDownloadingGame = false;
            this.isInstallingGame = false;
            this.gameNameInProgress = null;
            this.downloadPercent = null;
          } else if (message.type === 'downloadProgress') {
            this.downloadPercent = message.value;

            if (message.value >= 100) {
              this.isDownloadingGame = false;
            }
          } else if (
            message.error &&
            [
              'Command failed',
              'Install timed out',
              'Failed retreiving game installer download url',
            ].indexOf(message.error) !== -1
          ) {
            reject(message.error);
            this.removeEventListener('message', listenerHandle);
          }
        } catch (err) {}
      };

      try {
        this.downloadPercent = 0;
        this.isDownloadingGame = true;
        this.isInstallingGame = true;
        this.gameNameInProgress = command.params?.gameName;
        const message = JSON.stringify(command);
        this.socket.send(message);
        this.dispatchEvent({
          type: 'message',
          message: {
            type: 'startingInstall',
          },
        });

        // UA tracking
        this.$ua.trackGameInstallBeginEvent({
          userAgent: navigator.userAgent,
          game: this.gameNameInProgress,
        });
      } catch (err) {
        let errMsg = 'Failed to install game.';
        if (err instanceof Error) {
          errMsg = err.message;
        }

        // UA tracking
        this.$ua.trackGameInstallErrorEvent({
          userAgent: navigator.userAgent,
          game: this.gameNameInProgress,
          message: errMsg,
          launcherCurrentVersion: this.currentVersion,
        });

        this.isDownloadingGame = false;
        this.isInstallingGame = false;
        this.gameNameInProgress = null;

        return reject(errMsg);
      }
    });
  }

  async updateGame(
    command: IGameLauncherCommand,
    onDownloadProgress: (percent: number) => void,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.socket.readyState !== WebSocket.OPEN) {
        const errMsg = 'Not connected';
        // UA tracking
        this.$ua.trackGameUpdateErrorEvent({
          userAgent: navigator.userAgent,
          game: command.params?.gameName,
          message: errMsg,
          launcherCurrentVersion: this.currentVersion,
        });

        return reject(errMsg);
      }

      try {
        this.downloadPercent = 0;
        this.isDownloadingGame = true;
        this.isInstallingGame = true;
        this.gameNameInProgress = command.params?.gameName;

        const message = JSON.stringify(command);

        // UA tracking
        this.$ua.trackGameUpdateBeginEvent({
          userAgent: navigator.userAgent,
          game: this.gameNameInProgress,
        });

        this.socket.send(message);
        this.dispatchEvent({
          type: 'message',
          message: {
            type: 'startingInstall',
          },
        });
      } catch (err) {
        let errMsg = 'Failed to update game.';
        if (err instanceof Error) {
          errMsg = err.message;
        }

        // UA tracking
        this.$ua.trackGameUpdateErrorEvent({
          userAgent: navigator.userAgent,
          game: this.gameNameInProgress,
          launcherCurrentVersion: this.currentVersion,
          message: errMsg,
          error: err,
        });

        this.isDownloadingGame = false;
        this.isInstallingGame = false;
        this.gameNameInProgress = null;

        return reject(errMsg);
      }
    });
  }

  async updateLauncher(
    command: IGameLauncherCommand,
    onDownloadProgress: (percent: number) => void,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.socket.readyState !== WebSocket.OPEN) {
        const errMsg = 'Not connected';

        // UA tracking
        this.$ua.trackLauncherUpdateErrorEvent({
          userAgent: navigator.userAgent,
          message: errMsg,
          currentVersion: this.currentVersion,
        });

        return reject(errMsg);
      }

      this.socket.onmessage = (evt: any) => {
        try {
          const message = JSON.parse(evt.data);

          if (message.type === 'complete') {
            // UA tracking
            this.$ua.trackLauncherUpdateCompleteEvent({
              userAgent: navigator.userAgent,
              currentVersion: this.currentVersion,
            });

            resolve(message);
          } else if (message.type === 'downloadProgress') {
            // UA tracking
            this.$ua.trackLauncherUpdateProgressEvent({
              userAgent: navigator.userAgent,
              currentVersion: this.currentVersion,
              progress: message.value,
            });

            onDownloadProgress(message.value);
          } else if (message.error) {
            if (message.error === 'Command failed') {
              reject(message.error);
            } else if (message.error === 'Install timed out') {
              reject(message.error);
            }
          }
        } catch (err) {
          // UA tracking
          this.$ua.trackLauncherUpdateErrorEvent({
            userAgent: navigator.userAgent,
            error: err,
            currentVersion: this.currentVersion,
          });
        }
      };

      try {
        const message = JSON.stringify(command);

        // UA tracking
        this.$ua.trackLauncherUpdateBeginEvent({
          userAgent: navigator.userAgent,
          currentVersion: this.currentVersion,
        });

        this.socket.send(message);
      } catch (err) {
        let errMsg = 'Failed to update launcher.';
        if (err instanceof Error) {
          errMsg = err.message;
        }
        // UA tracking
        this.$ua.trackLauncherUpdateErrorEvent({
          userAgent: navigator.userAgent,
          error: err,
          message: errMsg,
          currentVersion: this.currentVersion,
        });

        return reject(errMsg);
      }
    });
  }

  addEventListener(type: string, callback: (evt: any) => void) {
    if (!(type in this.listeners)) {
      this.listeners[type] = [];
    }
    this.listeners[type].push(callback);
  }

  removeEventListener(type: string, callback: (evt: any) => void) {
    if (!(type in this.listeners)) {
      return;
    }
    const stack = this.listeners[type];
    for (let i = 0, l = stack.length; i < l; i++) {
      if (stack[i] === callback) {
        stack.splice(i, 1);
        return;
      }
    }
  }

  dispatchEvent(event: any) {
    if (!(event.type in this.listeners)) {
      return true;
    }
    const stack = this.listeners[event.type].slice();

    for (let i = 0, l = stack.length; i < l; i++) {
      stack[i].call(this, event);
    }
    return !event.defaultPrevented;
  }
}

// export default new GameLauncherService();
